import { Injectable } from '@angular/core';
import { ControlType, FileControlValue, LineItemModel } from '../models/template.model';
import {
  parseValidationConfig,
  TextValidationConfig,
  NumericValidationConfig,
  FileValidationConfig,
  DateValidationConfig,
} from '../models/validation-config.model';

@Injectable({ providedIn: 'root' })
export class ValidationService {

  /** Returns an array of error messages for the given line item and its current value. */
  validate(lineItem: LineItemModel, value: any): string[] {
    const errors: string[] = [];
    if (lineItem.isRequired && this.isMissingRequiredValue(lineItem, value)) {
      errors.push('This field is required');
    }

    const cfg = lineItem.validationConfig;
    if (!cfg) return errors;

    switch (lineItem.controlTypeId) {
      case ControlType.TextBox:
      case ControlType.TextArea: {
        const config = parseValidationConfig<TextValidationConfig>(cfg);
        if (!config) break;
        const str: string = value ?? '';
        if (config.minLength !== undefined && str.length < config.minLength) {
          errors.push(`Minimum ${config.minLength} character${config.minLength !== 1 ? 's' : ''} required`);
        }
        if (config.maxLength !== undefined && str.length > config.maxLength) {
          errors.push(`Maximum ${config.maxLength} character${config.maxLength !== 1 ? 's' : ''} allowed`);
        }
        if (config.pattern && str) {
          try {
            if (!new RegExp(config.pattern).test(str)) {
              errors.push(config.patternMessage || 'Invalid format');
            }
          } catch { /* ignore invalid regex */ }
        }
        break;
      }

      case ControlType.Integer:
      case ControlType.Decimal: {
        const config = parseValidationConfig<NumericValidationConfig>(cfg);
        if (!config) break;
        const num = value as number | null;
        if (num !== null && num !== undefined) {
          if (config.min !== undefined && num < config.min) {
            errors.push(`Minimum value is ${config.min}`);
          }
          if (config.max !== undefined && num > config.max) {
            errors.push(`Maximum value is ${config.max}`);
          }
        }
        break;
      }

      case ControlType.Picture:
      case ControlType.Attachment:
      case ControlType.Signature: {
        const config = parseValidationConfig<FileValidationConfig>(cfg);
        if (!config) break;
        const fileVal = value as FileControlValue | null;
        const totalCount = (fileVal?.newFiles?.length ?? 0) + (fileVal?.existingFileIds?.length ?? 0);
        if (config.minCount !== undefined && totalCount < config.minCount) {
          errors.push(`At least ${config.minCount} file${config.minCount !== 1 ? 's' : ''} required`);
        }
        if (config.maxCount !== undefined && totalCount > config.maxCount) {
          errors.push(`Maximum ${config.maxCount} file${config.maxCount !== 1 ? 's' : ''} allowed`);
        }
        break;
      }

      case ControlType.DatePicker:
      case ControlType.DateTimePicker: {
        const config = parseValidationConfig<DateValidationConfig>(cfg);
        if (!config || !value) break;
        const dateVal = new Date(value);
        if (isNaN(dateVal.getTime())) break;

        if (config.minDate) {
          const min = config.minDate === 'today' ? new Date() : new Date(config.minDate);
          min.setHours(0, 0, 0, 0);
          if (dateVal < min) {
            errors.push(`Date must be on or after ${config.minDate === 'today' ? 'today' : config.minDate}`);
          }
        }
        if (config.maxDate) {
          const max = config.maxDate === 'today' ? new Date() : new Date(config.maxDate);
          max.setHours(23, 59, 59, 999);
          if (dateVal > max) {
            errors.push(`Date must be on or before ${config.maxDate === 'today' ? 'today' : config.maxDate}`);
          }
        }
        break;
      }
    }

    return errors;
  }

  private isMissingRequiredValue(lineItem: LineItemModel, value: any): boolean {
    switch (lineItem.controlTypeId) {
      case ControlType.TextBox:
      case ControlType.TextArea:
        return typeof value !== 'string' || value.trim() === '';
      case ControlType.Select:
      case ControlType.ImpactedArea:
        if (Array.isArray(value)) {
          return this.normalizeArrayValue(value).length === 0;
        }
        return typeof value !== 'string' || value.trim() === '';
      case ControlType.RichText:
        return this.isRichTextEmpty(value);
      case ControlType.CheckBox:
        return value !== true;
      case ControlType.DatePicker:
      case ControlType.DateTimePicker:
      case ControlType.Tasks:
        return value === null || value === undefined || value === '' || value === 0;
      case ControlType.Integer:
      case ControlType.Decimal:
      case ControlType.Judgement:
        return value === null || value === undefined || value === '';
      case ControlType.Users:
      case ControlType.QRScanner:
        return this.normalizeArrayValue(value).length === 0;
      case ControlType.Skids: {
        if (value === null || value === undefined || value === '') {
          return true;
        }
        const parsed = Number(value);
        return !Number.isFinite(parsed) || !(parsed === -1 || parsed > 0);
      }
      case ControlType.Attachment:
      case ControlType.Picture:
      case ControlType.Signature:
        return this.getFileCount(value as FileControlValue | null) === 0;
      case ControlType.DigitalSignature:
        return !Array.isArray(value?.signatures) || value.signatures.length === 0;
      case ControlType.Table:
      case ControlType.Weather:
        return !Array.isArray(value) || value.length === 0;
      default:
        return value === null || value === undefined || value === '' ||
          (Array.isArray(value) && value.length === 0);
    }
  }

  private getFileCount(value: FileControlValue | null): number {
    return (value?.newFiles?.length ?? 0) + (value?.existingFileIds?.length ?? 0);
  }

  private normalizeArrayValue(value: any): any[] {
    if (Array.isArray(value)) {
      return value.filter(item => item !== null && item !== undefined && item !== '');
    }

    if (typeof value !== 'string') {
      return [];
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(item => item !== null && item !== undefined && item !== '');
      }
    } catch {
      return [trimmed];
    }

    return [];
  }

  private isRichTextEmpty(value: any): boolean {
    if (typeof value !== 'string') {
      return true;
    }

    const stripped = value
      .replace(/&nbsp;/gi, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return stripped.length === 0;
  }

  /** Returns a concise hint string describing the configured constraints for display below the control. */
  getHint(lineItem: LineItemModel): string {
    const cfg = lineItem.validationConfig;
    if (!cfg) return '';
    const parts: string[] = [];

    switch (lineItem.controlTypeId) {
      case ControlType.TextBox:
      case ControlType.TextArea: {
        const config = parseValidationConfig<TextValidationConfig>(cfg);
        if (!config) break;
        if (config.minLength !== undefined) parts.push(`Min ${config.minLength} chars`);
        if (config.maxLength !== undefined) parts.push(`Max ${config.maxLength} chars`);
        if (config.pattern) parts.push('Must match pattern');
        break;
      }
      case ControlType.Integer:
      case ControlType.Decimal: {
        const config = parseValidationConfig<NumericValidationConfig>(cfg);
        if (!config) break;
        if (config.min !== undefined) parts.push(`Min ${config.min}`);
        if (config.max !== undefined) parts.push(`Max ${config.max}`);
        break;
      }
      case ControlType.Picture:
      case ControlType.Attachment:
      case ControlType.Signature: {
        const config = parseValidationConfig<FileValidationConfig>(cfg);
        if (!config) break;
        if (config.minCount !== undefined) parts.push(`Min ${config.minCount} files`);
        if (config.maxCount !== undefined) parts.push(`Max ${config.maxCount} files`);
        break;
      }
      case ControlType.DatePicker:
      case ControlType.DateTimePicker: {
        const config = parseValidationConfig<DateValidationConfig>(cfg);
        if (!config) break;
        if (config.minDate) parts.push(`From ${config.minDate}`);
        if (config.maxDate) parts.push(`Until ${config.maxDate}`);
        break;
      }
    }

    return parts.join(' · ');
  }
}
